import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { IconHighlight, ModalLayout, SearchInput, Skeleton, StatusMessage, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Plus, TriangleAlert } from 'lucide-react';

import { useBuilderMembers } from '../../hooks/useBuilderMembers';
import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import MemberPickRow from '../shared/MemberPickRow';
import { NO_HEADER_LINE } from '../shared/modalClasses';

const TITLE_ID = 'assign-role-members-title';

/**
 * "Assign members" dialog of the role detail: the members who do not hold the role yet, with a
 * search. Picking one assigns the role at once (`onAssign` resolves `true` when it worked and the
 * dialog closes). Mount it only while open so the list is read fresh.
 *
 * @param {object} props
 * @param {object} props.role UI role (see `mapApiRoleToUi`)
 * @param {string[]} props.memberIds Ids of the members who already hold the role.
 * @param {(member: object) => Promise<boolean>} props.onAssign
 * @param {() => void} props.onClose
 */
const AssignRoleMembersModal = ({ role, memberIds, onAssign, onClose }) => {
  const { t } = useTranslation('people-access');
  const { members, isLoading, error } = useBuilderMembers();
  const [query, setQuery] = useState('');
  const [assigning, setAssigning] = useState(false);
  // The ref blocks a second pick before React re-renders with `assigning`; Esc waits for it too.
  const assigningRef = useRef(false);

  const handleEscape = useCallback(() => {
    if (!assigningRef.current) onClose();
  }, [onClose]);
  useDialogKeyboard(true, handleEscape, TITLE_ID);

  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return members
      .filter((member) => !memberIds.includes(member.id))
      .filter((member) => !needle || `${member.name} ${member.email}`.toLowerCase().includes(needle));
  }, [members, memberIds, query]);

  const handleChange = useCallback((event) => setQuery(event.target.value), []);
  const handleClear = useCallback(() => setQuery(''), []);
  const handlePick = useCallback(
    async (memberId) => {
      const member = members.find((m) => m.id === memberId);
      if (!member || assigningRef.current) return;
      assigningRef.current = true;
      setAssigning(true);
      let ok = false;
      try {
        ok = await onAssign(member);
      } finally {
        assigningRef.current = false;
        setAssigning(false);
      }
      if (ok) onClose();
    },
    [members, onAssign, onClose],
  );

  let body;
  if (isLoading) body = <Skeleton height="200px" borderRadius="12px" />;
  else if (error) body = <StatusMessage icon={TriangleAlert} title={t('roles.detail.members.assign.loadError')} />;
  else if (!candidates.length) body = <p className="px-1 text-sm text-content-muted">{t('roles.detail.members.assign.empty')}</p>;
  else {
    body = (
      <ul className="scrollbar-aimsos-auto-sm flex max-h-72 flex-col gap-1 overflow-y-auto" data-testid="assign-role-members">
        {candidates.map((member) => (
          <li key={member.id}>
            <MemberPickRow
              member={member}
              avatarSize="md"
              showActiveStatus={false}
              onPick={handlePick}
              ariaLabel={t('roles.detail.members.assign.pickAria', { name: member.name })}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ModalLayout
      isOpen
      onClose={onClose}
      useBodyPortal
      title={t('roles.detail.members.assign.title')}
      subtitle={t('roles.detail.members.assign.subtitle', { role: role.name })}
      closeAriaLabel={t('roles.detail.members.assign.close')}
      closeDisabled={assigning}
      titleId={TITLE_ID}
      titlePrefix={<IconHighlight icon={Plus} bgColor="Primary" iconColor="PrimaryAlt" size="medium" />}
      panelClassName={NO_HEADER_LINE}
      maxWidthClassName="max-w-[90vw] md:max-w-[min(60vw,640px)]"
    >
      <div className="flex flex-col gap-3 px-6 pb-2">
        <SearchInput
          value={query}
          placeholder={t('roles.detail.members.assign.searchPlaceholder')}
          aria-label={t('roles.detail.members.assign.searchAria')}
          onChange={handleChange}
          onClear={handleClear}
          autoFocus
        />
        {body}
      </div>
      <div className="flex justify-end px-6 py-4">
        <Button variant="secondary" size="md" onClick={onClose} disabled={assigning}>
          {t('roles.detail.members.assign.cancel')}
        </Button>
      </div>
    </ModalLayout>
  );
};

export default memo(AssignRoleMembersModal);
